import { ComponentIds, Vector } from '@inter-ikea-core/core';
import { ShapeTypeEnum } from '../../../types';
import EventComponent from '../../components/EventComponent';
import HelpLinesOuterMeasurementsComponent from '../../components/HelpLinesOuterMeasurementsComponent';
import { PlannerEcsMultiSystem } from '../System';
import { Corner } from './Corner';
import { getLabelPositions, getShapeCenter } from './HelpLinesLineLabelUtils';
import { getNextCorner, movePolygonToCenter, resetPolygonToXCenterAndYFloor, toCoreVector } from './HelpLinesUtils';
import { SlopingShape } from './SlopingShape';
import { SlopingShapeLeft } from './SlopingShapeLeft';
import { SquaredShape } from './SquaredShape';
import { SquaredShapeRightCorner } from './SquaredShapeRightCorner';
import HelpLinesComponentVisible from '../../components/HelpLinesComponentVisible';
import HelpLinesComponentEditMode from '../../components/HelpLinesComponentEditMode';
import HelpLinesComponentShape from '../../components/HelpLinesComponentShape';
import { isFacingCamera } from '../../../utils/camera';
import { NoShape } from './NoShape';
import RoomChangeCompleteComponent from '../../components/RoomChangeCompleteComponent';
import HelpLinesColliderBoxesComponent from '../../components/HelpLinesColliderBoxesComponent';
export const helpLinesScale = 1000;
export const interactiveColor = { r: 0 / 255, g: 88 / 255, b: 163 / 255 };
export const nonInteractiveColor = { r: 0 / 255, g: 0 / 255, b: 0 / 255 };
export class HelpLinesSystem extends PlannerEcsMultiSystem {
    constructor(coreApi) {
        super(coreApi, 'HelpLinesSystem', [
            EventComponent.id,
            HelpLinesComponentVisible.id,
            HelpLinesComponentEditMode.id,
            HelpLinesComponentShape.id,
            RoomChangeCompleteComponent.id
        ]);
        this.shapeConfig = [];
        this.currentShapeType = ShapeTypeEnum.off;
        this.coreApi = coreApi;
        this.labelPositions = [];
        this.corners = [];
        this.wallDimensions = { minX: 0, maxX: 0, maxY: 0, z: 0 };
        this.currentShape = this.initDefaultShape();
        this.previousShape = {
            shapeType: ShapeTypeEnum.off,
            cornerPositions: [],
            roomCenterPosition: new Vector(0, 0, 0)
        };
        this.visible = false;
        this.editMode = false;
        this.wallVisible = true;
    }
    init() {
        this.coreApi.subscribe('load', () => {
            this.setWallDimensions();
            //if shape is square corner right or left and room width has changed
            //then move the handles to the walls.
            this.updateAllCornersAndLines();
        });
        this.coreApi.subscribe('reset', () => {
            this.disable();
            this.clearShape();
        });
    }
    onAdd(entity, newComponent) {
        switch (newComponent.typeId) {
            case HelpLinesComponentVisible.id:
                if (newComponent.visible) {
                    this.enable();
                }
                break;
            case HelpLinesComponentEditMode.id:
                if (newComponent.editMode) {
                    this.enableEditMode();
                }
                else {
                    this.disableEditMode();
                }
                break;
            case HelpLinesComponentShape.id:
                const { shapeType, dimensions } = newComponent;
                this.setShape(shapeType);
                if (dimensions.length > 0) {
                    this.setShapeDimensions(dimensions);
                }
                break;
            case RoomChangeCompleteComponent.id:
                this.moveShapeAfterRoomChange();
        }
    }
    onUpdate(entity, oldComponent, newComponent) {
        switch (newComponent.typeId) {
            case HelpLinesComponentVisible.id:
                const enabled = newComponent.visible;
                if (enabled !== this.visible) {
                    if (enabled) {
                        this.enable();
                    }
                    else {
                        this.disable();
                    }
                }
                break;
            case HelpLinesComponentEditMode.id:
                if (newComponent.editMode) {
                    this.enableEditMode();
                }
                else {
                    this.disableEditMode();
                }
                break;
            case HelpLinesComponentShape.id:
                const { shapeType, dimensions } = newComponent;
                this.setShape(shapeType);
                if (dimensions.length > 0) {
                    this.setShapeDimensions(dimensions);
                }
                break;
            case EventComponent.id:
                if (this.visible && newComponent.event === 'update') {
                    this.setLineCenterPoints();
                    this.onCameraMovement();
                }
                break;
            case RoomChangeCompleteComponent.id:
                this.moveShapeAfterRoomChange();
        }
    }
    onRemove() { }
    getBackWallPosition() {
        const wall = this.coreApi.camera.getWallFacingDirection(new Vector(0, 0, -1));
        const wallPosition = wall.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)?.transform.position;
        return wallPosition;
    }
    createCorners(viewer) {
        // Fires when a corner is moved and the delta movement from viewer is passed to the corner that moves the corresponding distance
        const handleCornerMove = (corner, delta) => {
            this.moveCorner(viewer, corner, delta);
            this.coreApi.emit.onHelpLinesShapeResize();
        };
        // Fires when a corner has been moved and the user has released the mouse button
        const handleCornerMoveEnd = () => {
            this.snapAllCorners(viewer);
            this.coreApi.emit.onHelpLinesShapeResizeFinished();
            // Update component that holds the shape in the scene. This is used by HelpLinesColliderBoxSystem
            const scene = this.coreApi.entities.getScene();
            scene?.addComponent(new HelpLinesColliderBoxesComponent(this.getShapeDimensions()));
        };
        // Clear all current corners
        this.corners = [];
        // Create new corners based on the shape configuration and the back wall center position
        this.shapeConfig.forEach((point, i) => {
            this.corners.push(new Corner(point.point, this.shapeConfig[i !== this.shapeConfig.length - 1 ? i + 1 : 0].point, viewer, handleCornerMove, handleCornerMoveEnd, point.measurementLabelHidden, point.noHandle, point.rotation, this.getBackWallPosition(), this.editMode));
        });
        this.updateAllCornersAndLines();
    }
    /**
     * Set the back wall dimensions to local variable
     */
    setWallDimensions() {
        const wall = this.coreApi.camera.getWallFacingDirection(new Vector(0, 0, -1));
        if (!wall)
            return;
        const worldDragSurfacesComponent = wall.getComponent(ComponentIds.WORLD_DRAG_SURFACES_COMPONENT_ID);
        const axisAlignedBoundingBox = worldDragSurfacesComponent?.surfaces[0].surface.axisAlignedBoundingBox;
        const height = this.coreApi.kvadratRoom?.getRoomSize()?.height;
        if (!axisAlignedBoundingBox)
            return;
        this.wallDimensions = {
            minX: axisAlignedBoundingBox.min.X / 1000,
            maxX: axisAlignedBoundingBox.max.X / 1000,
            maxY: height ? height / 1000 : 0,
            z: axisAlignedBoundingBox.min.Z / 1000
        };
    }
    getShapeXDimensions() {
        let min = 0;
        let max = 0;
        this.corners.forEach(corner => {
            const { x } = corner.getPosition();
            min = Math.min(min, x);
            max = Math.max(max, x);
        });
        return { min, max };
    }
    getShapeDimensions() {
        return this.corners.map(c => toCoreVector(c.getPosition(), helpLinesScale));
    }
    getShapeWidth() {
        const minMax = this.corners.reduce((size, c) => {
            const x = c.getPosition().x;
            if (x < size.min)
                size.min = x;
            if (x > size.max)
                size.max = x;
            return size;
        }, { min: this.corners[0]?.getPosition().x, max: this.corners[0]?.getPosition().x });
        return { min: minMax.min * helpLinesScale, max: minMax.max * helpLinesScale };
    }
    getShapeHeight() {
        const minMax = this.corners.reduce((size, c) => {
            const y = c.getPosition().y;
            if (y < size.min)
                size.min = y;
            if (y > size.max)
                size.max = y;
            return size;
        }, { min: this.corners[0]?.getPosition().y, max: this.corners[0]?.getPosition().y });
        return (minMax.max - minMax.min) * helpLinesScale;
    }
    moveCorner(viewer, corner, delta) {
        this.currentShape.moveHandle(this.corners, corner, delta, this.wallDimensions);
        this.updateAllLinePositions(viewer);
    }
    /**
     * Repositions all corners to closest rounded centimeter
     * @param viewer The viewer
     */
    snapAllCorners(viewer) {
        this.corners.forEach(corner => {
            corner.snapPosition();
        });
        this.updateAllLinePositions(viewer);
    }
    /**
     * Updates all lines so they start and end in a corner
     * Also updates line center points so labels moves accordingly
     * @param viewer The viewer
     */
    updateAllLinePositions(viewer) {
        this.corners.forEach((corner, i) => {
            viewer.update_ui_line(corner.lineId, corner.handle.position, getNextCorner(this.corners, i).getPosition());
        });
        this.setLineCenterPoints();
    }
    /**
     * Updates the labels with the distance of the line
     * as well as the position of the label.
     */
    setLineCenterPoints() {
        const shapeCornerPoints = this.corners.map((corner, i) => ({
            start: corner.getPosition(),
            end: getNextCorner(this.corners, i).getPosition(),
            hidden: corner.measurementLabelHidden,
            facingCamera: this.cornerIsFacingCamera(corner)
        }));
        const shapeCenter = getShapeCenter(shapeCornerPoints);
        if (this.corners.length > 0) {
            this.labelPositions = getLabelPositions(this.coreApi, shapeCornerPoints, shapeCenter, this.editMode);
            this.coreApi.emit.onHelpLinesUpdated();
        }
    }
    // Used by HelpLinesWidget
    getHelpLineLabels() {
        return this.wallVisible ? this.labelPositions : [];
    }
    /**
     * Initializes a default shape when creating this class.
     * @returns Default shape type
     */
    initDefaultShape() {
        const shapeType = new NoShape();
        this.shapeConfig = shapeType.getShape();
        return shapeType;
    }
    /**
     * When shape type is changed to off we store the current shape.
     * If the user switches back to the same shape again we want to
     * be able to restore it the way it was.
     * @param shapeType currently set shape type.
     */
    storePreviousShape(shapeType) {
        if (shapeType === ShapeTypeEnum.off) {
            const roomCenterPosition = this.coreApi.kvadratRoom?.getRoomCenterPosition();
            this.previousShape = {
                shapeType: this.currentShapeType,
                cornerPositions: this.getShapeDimensions(),
                roomCenterPosition: roomCenterPosition
                    ? new Vector(roomCenterPosition.x, roomCenterPosition.y, roomCenterPosition.z)
                    : new Vector(0, 0, 0)
            };
        }
    }
    /**
     * If user selects the same shape as before it was set to off
     * we load the previous shape configuration and apply it again.
     * @param shapeType currently set shape type.
     */
    setPreviousShape(shapeType) {
        const roomCenterPosition = this.coreApi.kvadratRoom?.getRoomCenterPosition();
        if (this.previousShape.shapeType === this.currentShapeType &&
            this.previousShape.cornerPositions.length > 0) {
            this.setShapeDimensions(this.previousShape.cornerPositions);
            if (roomCenterPosition?.x !== this.previousShape.roomCenterPosition.X ||
                roomCenterPosition?.y !== this.previousShape.roomCenterPosition.Y) {
                this.moveShapeAfterRoomChange();
            }
        }
        if (shapeType !== ShapeTypeEnum.off) {
            this.previousShape = {
                shapeType,
                cornerPositions: [],
                roomCenterPosition: roomCenterPosition
                    ? new Vector(roomCenterPosition.x, roomCenterPosition.y, roomCenterPosition.z)
                    : new Vector(0, 0, 0)
            };
        }
    }
    /**
     * Called from room config menu where user selects a shape
     * @param shapeType User selected shape
     */
    setShape(shapeType) {
        if (this.currentShapeType !== shapeType) {
            this.storePreviousShape(shapeType);
            switch (shapeType) {
                case ShapeTypeEnum.squared:
                    this.currentShape = new SquaredShape();
                    break;
                case ShapeTypeEnum.slopedLeft:
                    this.currentShape = new SlopingShapeLeft();
                    break;
                case ShapeTypeEnum.sloped:
                    this.currentShape = new SlopingShape();
                    break;
                case ShapeTypeEnum.squaredCornerRight:
                    this.currentShape = new SquaredShapeRightCorner(this.wallDimensions.maxX / 2 / helpLinesScale);
                    break;
                case ShapeTypeEnum.off:
                    this.currentShape = this.initDefaultShape();
                    this.shapeConfig = [];
                    break;
            }
            this.currentShapeType = shapeType;
            if (this.currentShape) {
                if (this.currentShapeType !== ShapeTypeEnum.off) {
                    this.shapeConfig = this.currentShape.getShape();
                    this.resetCurrentShape();
                    this.setPreviousShape(shapeType);
                }
                else {
                    this.clearShape();
                }
            }
        }
    }
    /**
     * Used by other parts of the application to check which is the current
     * shape type
     * @returns Current shape type
     */
    getShape() {
        return this.currentShapeType;
    }
    /**
     * Called when user enables help lines system with toggle in room config menu
     */
    enable() {
        this.visible = true;
        if (this.corners.length === 0) {
            const viewer = this.coreApi.ipexViewer;
            this.setWallDimensions();
            this.createCorners(viewer);
        }
        else {
            const viewer = this.coreApi.ipexViewer;
            this.corners.forEach(corner => {
                viewer.set_ui_line_visible(corner.lineId, true);
                viewer.set_visible(corner.handle.id, !corner.handleNotVisible);
            });
        }
        const scene = this.coreApi.getServices().sceneManager.scene;
        scene?.addComponent(new HelpLinesOuterMeasurementsComponent());
    }
    /**
     * Called when user disables help lines system with toggle in room config menu
     */
    disable() {
        this.visible = false;
        const viewer = this.coreApi.ipexViewer;
        this.corners.forEach(corner => {
            viewer.set_ui_line_visible(corner.lineId, false);
            viewer.set_visible(corner.handle.id, false);
        });
        const scene = this.coreApi.getServices().sceneManager.scene;
        scene?.removeComponent(HelpLinesOuterMeasurementsComponent.id);
    }
    /**
     * Shows corner handles and sets lines to a blue color.
     * If there is a conflict the lines will be set to red.
     * Called when user enters room config menu
     */
    enableEditMode() {
        this.editMode = true;
        // After changing to edit mode we need to reset labels so the ones that are hidden is displayed in edit mode
        this.setLineCenterPoints();
        const viewer = this.coreApi.ipexViewer;
        if (this.visible) {
            this.corners.forEach(corner => {
                viewer.set_visible(corner.handle.id, !corner.handleNotVisible);
                corner.setLineColor(viewer, interactiveColor);
            });
        }
    }
    /**
     * Hides corner handles and sets lines to a black color.
     * If there is a conflict the lines will be set to red.
     * Called when user exits room config menu
     */
    disableEditMode() {
        this.editMode = false;
        // After changing from edit mode we need to reset labels so we hide the ones that should be hidden.
        this.setLineCenterPoints();
        const viewer = this.coreApi.ipexViewer;
        this.corners.forEach(corner => {
            viewer.set_visible(corner.handle.id, false);
            corner.setLineColor(viewer, nonInteractiveColor);
        });
    }
    /**
     *
     * @param visible Used from screenshot to temporarily hide lines while taking screenshot
     */
    setLinesVisibility(visible) {
        const viewer = this.coreApi.ipexViewer;
        this.corners.forEach(corner => {
            viewer.set_visible(corner.handle.id, this.editMode && visible ? !corner.handleNotVisible : false);
            viewer.set_ui_line_visible(corner.lineId, visible);
        });
    }
    /**
     * Resets current shape to default values.
     * Used when changing shape
     */
    resetCurrentShape() {
        const viewer = this.coreApi.ipexViewer;
        this.clearShape();
        this.createCorners(viewer);
        this.updateAllCornersAndLines();
    }
    /**
     * Also Called from menu when going back to gallery.
     * Clearing shape to prevent lines from magically
     * beeing set to visible by removing them completely.
     */
    clearShape() {
        const viewer = this.coreApi.ipexViewer;
        this.corners.forEach(corner => {
            if (corner.handle)
                viewer.delete_object(corner.handle.id);
            if (corner.lineId)
                viewer.delete_ui_line(corner.lineId);
        });
        this.corners = [];
    }
    /**
     * Used to update corners and lines when changing room size or
     * changing shape so corners and lines are recalculated to not
     * end up outside of room
     */
    updateAllCornersAndLines() {
        this.corners.forEach(corner => {
            this.moveCorner(this.coreApi.ipexViewer, corner, { x: 0, y: 0, z: 0 });
        });
        this.updateAllLinePositions(this.coreApi.ipexViewer);
        const scene = this.coreApi.entities.getScene();
        scene?.addComponent(new HelpLinesColliderBoxesComponent(this.getShapeDimensions()));
    }
    getCurrentShapePointsForAnalytics() {
        return this.corners.map(c => [c.getPosition().x, c.getPosition().y]);
    }
    setShapeDimensions(dimensions) {
        //Update all corners with the saved positions from the VPC
        if (this.corners.length === dimensions.length) {
            for (let i = 0; i < dimensions.length; i++) {
                this.corners[i].moveTo(dimensions[i].X / helpLinesScale, dimensions[i].Y / helpLinesScale, dimensions[i].Z / helpLinesScale);
            }
        }
        this.updateAllCornersAndLines();
    }
    /**
     * Hide wall shape lines when they are not facing the camera.
     */
    onCameraMovement() {
        this.corners.forEach(c => {
            const facingCam = this.cornerIsFacingCamera(c);
            this.coreApi.ipexViewer.set_ui_line_visible(c.lineId, facingCam);
            this.coreApi.ipexViewer.set_visible(c.handle.id, this.editMode ? facingCam : false);
        });
    }
    cornerIsFacingCamera(corner) {
        const cornerRotation = this.coreApi.ipexViewer.get_rotation(corner.handle.id);
        return isFacingCamera(this.coreApi, cornerRotation.y);
    }
    moveShapeAfterRoomChange() {
        this.setWallDimensions();
        const shapeReset = resetPolygonToXCenterAndYFloor(this.corners.map(c => c.getPosition()));
        const center = this.coreApi.kvadratRoom?.getRoomCenterPosition();
        if (!center)
            return;
        const movedShape = movePolygonToCenter(shapeReset, { ...center, z: this.wallDimensions.z });
        this.corners.forEach((corner, i) => {
            corner.moveTo(movedShape[i].x, movedShape[i].y, this.wallDimensions.z);
        });
        this.updateAllCornersAndLines();
        this.coreApi.emit.onHelpLinesShapeResizeFinished();
    }
}
//# sourceMappingURL=HelpLinesSystem.js.map